import React, {
    useState, useEffect, useRef, useMemo,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Dropdown, Button } from 'react-bootstrap';

import { useDebounce, useOnClickOutside } from '../tools/hooks';
import reorder from '../tools/reorder-list';
import '../css/search-drag-list.css';

function SearchDragList({
    id, title, list, onChange, searchUrl,
}) {
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [copiedItem, setCopied] = useState('');

    const listRef = useRef(null);
    const containerRef = useRef(null);

    const debouncedSearch = useDebounce(search, 400);
    useOnClickOutside(listRef, clearSearchInput);

    useEffect(() => {
        if (debouncedSearch) {
            handleSearch(debouncedSearch);

            return;
        }

        setSearchResults(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const listIds = useMemo(() => list.reduce((acc, current) => [...acc, current.id], []), [list]);

    async function handleSearch(query) {
        setLoading(true);
        let results = await fetch(`${searchUrl}${searchUrl.includes('?') ? '&' : '?'}search=${encodeURI(query)}`);
        results = await results.json();

        setLoading(false);

        if (!results.count) {
            setSearchResults(null);

            return;
        }

        setSearchResults(results.results);
    }

    function handleSearchInput(e) {
        const { value } = e.currentTarget;

        setSearch(value);
    }

    function handleDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const items = reorder(
            list,
            result.source.index,
            result.destination.index,
        );

        onChange({
            currentTarget: {
                id,
                value: items,
            },
        });
    }

    function handleItemAdd(newItem) {
        onChange({
            currentTarget: {
                id,
                value: [
                    ...list,
                    newItem,
                ],
            },
        });

        scrollContainerBottom();
        clearSearchInput();
    }

    function handleNameCopy(e) {
        const { name } = e.currentTarget.dataset;

        if (window.navigator.clipboard) {
            window.navigator.clipboard.writeText(name).then(() => {
                setCopied(name);

                setTimeout(() => {
                    setCopied('');
                }, 1000);
            });
        }
    }

    function handleItemDelete(e) {
        e.preventDefault();

        const { id: itemId } = e.currentTarget;
        const newList = list.filter((item) => item.id !== +itemId);

        onChange({
            currentTarget: {
                id,
                value: newList,
            },
        });
    }

    function clearSearchInput() {
        setSearch('');
    }

    function scrollContainerBottom() {
        const { current } = containerRef;

        if (!current) {
            return;
        }

        current.scrollTop = current.scrollHeight + 50;
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable">
                {(provided) => (
                    <div className="search-drag-list" ref={listRef}>
                        <p className="search-drag-list__title">
                            {title}
                            {' '}
                            <span className="search-drag-list__count">
                                (
                                {list.length}
                                )
                            </span>
                        </p>
                        <div
                            ref={containerRef}
                            className="search-drag-list__container"
                        >
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {list.map((item, i) => (
                                    <Draggable key={item.id} draggableId={item.id} index={i}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="search-drag-list__item"
                                            >
                                                {item.name || item.title}
                                                <div>
                                                    {copiedItem === item.name || copiedItem === item.title ? (
                                                        <Button
                                                            size="sm"
                                                            variant="link"
                                                            to="#"
                                                        >
                                                            <FontAwesomeIcon icon="check" />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="link"
                                                            id={item.id}
                                                            to="#"
                                                            onClick={handleNameCopy}
                                                            data-name={item.name || item.title}
                                                        >
                                                            <FontAwesomeIcon icon="copy" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        id={item.id}
                                                        to="#"
                                                        onClick={handleItemDelete}
                                                    >
                                                        <FontAwesomeIcon icon="trash" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        </div>
                        <input
                            className="search-drag-list__search"
                            type="search"
                            value={search}
                            onChange={handleSearchInput}
                            placeholder="Поиск…"
                        />
                        {debouncedSearch && searchResults && (
                            <Dropdown.Menu show>
                                <div>
                                    {searchResults && searchResults.map((searchItem) => !listIds.includes(searchItem.id) && (
                                        <Dropdown.Item
                                            key={searchItem.id}
                                            onClick={() => handleItemAdd(searchItem)}
                                        >
                                            {searchItem.name || searchItem.title}
                                        </Dropdown.Item>
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        )}
                        {debouncedSearch && !searchResults && !isLoading && (
                            <Dropdown.Menu show>
                                <div className="search-drag-list__nothing">
                                    К сожалению, ничего не найдено 😢
                                </div>
                            </Dropdown.Menu>
                        )}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
}

SearchDragList.defaultProps = {
    list: [],
};

export default SearchDragList;
